import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BadgeCheck,
  Check,
  CreditCard,
  Loader2,
  Palette,
  QrCode,
  RefreshCw,
  Utensils,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { subscriptionsApi } from "@/api/subscriptions";
import { cn } from "@/lib/utils";
import type { SubscriptionPlan } from "@/types";
import { RegistrationLogoutButton } from "./RegistrationLogoutButton";

const planLabels: Record<SubscriptionPlan["interval"], string> = {
  monthly: "Mensal",
  yearly: "Anual",
};

const planIntervalLabels: Record<SubscriptionPlan["interval"], string> = {
  monthly: "/mês",
  yearly: "/ano",
};

const planCtaLabels: Record<SubscriptionPlan["interval"], string> = {
  monthly: "Começar teste mensal",
  yearly: "Começar teste anual",
};

const includedFeatures: Array<{
  icon: LucideIcon;
  title: string;
  description: string;
}> = [
  {
    icon: QrCode,
    title: "Cardápio por QR code",
    description: "Publique seu menu para clientes acessarem pelo celular.",
  },
  {
    icon: RefreshCw,
    title: "Atualizações em tempo real",
    description: "Altere produtos, categorias e preços sem reimprimir nada.",
  },
  {
    icon: Utensils,
    title: "Produtos ilimitados",
    description: "Cadastre todos os pratos, bebidas e adicionais do restaurante.",
  },
  {
    icon: Palette,
    title: "Marca personalizada",
    description: "Use cor, logo e imagem do restaurante no cardápio.",
  },
];

const checkoutSteps = [
  "Você será redirecionado para o Stripe Checkout.",
  "Seu teste grátis começa imediatamente.",
  "A cobrança só começa depois do período de teste.",
];

function formatPrice(priceInCents: number, currency: string) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(priceInCents / 100);
}

function getYearlyValue(
  monthlyPlan: SubscriptionPlan | undefined,
  yearlyPlan: SubscriptionPlan | undefined
) {
  if (
    !monthlyPlan ||
    !yearlyPlan ||
    monthlyPlan.currency !== yearlyPlan.currency
  ) {
    return null;
  }

  const annualMonthlyPrice = monthlyPlan.price * 12;
  const annualSavings = annualMonthlyPrice - yearlyPlan.price;

  if (annualSavings <= 0) {
    return null;
  }

  return {
    monthlyEquivalent: yearlyPlan.price / 12,
    annualSavings,
    savingsPercentage: Math.round((annualSavings / annualMonthlyPrice) * 100),
  };
}

export function SubscriptionPage() {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    data: plans = [],
    isError: failedToLoadPlans,
    isLoading: isLoadingPlans,
  } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: subscriptionsApi.getPlans,
  });

  const handleSelectPlan = async (planId: string) => {
    setSelectedPlan(planId);
    setError(null);
    setIsLoading(true);

    try {
      const { url } = await subscriptionsApi.startCheckoutSession(planId);
      // eslint-disable-next-line react-hooks/immutability
      window.location.href = url;
    } catch {
      setError("Não foi possível iniciar o checkout. Tente novamente.");
      setIsLoading(false);
    }
  };

  const monthlyPlan = plans.find((plan) => plan.interval === "monthly");
  const yearlyPlan = plans.find((plan) => plan.interval === "yearly");
  const yearlyValue = getYearlyValue(monthlyPlan, yearlyPlan);

  if (isLoadingPlans) {
    return (
      <div className="min-h-screen bg-background px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-6xl justify-end">
          <RegistrationLogoutButton />
        </div>
        <div
          className="flex min-h-[65vh] flex-col items-center justify-center gap-3"
          role="status"
          aria-live="polite"
        >
          <Loader2
            className="h-8 w-8 animate-spin text-primary"
            aria-hidden="true"
          />
          <span className="text-sm text-muted-foreground">
            Carregando planos...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto flex w-full max-w-6xl justify-end">
        <RegistrationLogoutButton />
      </div>

      <main className="mx-auto w-full max-w-6xl py-10 sm:py-14">
        <header className="mx-auto mb-10 max-w-2xl text-center">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
            Etapa 3 de 3
          </p>
          <h1 className="text-3xl font-bold text-foreground text-balance sm:text-4xl">
            Escolha seu plano
          </h1>
          <p className="mt-3 text-base text-muted-foreground text-pretty sm:text-lg">
            Teste grátis por 14 dias. Cancele quando quiser.
          </p>
        </header>

        {error && (
          <Alert variant="destructive" className="mx-auto mb-6 max-w-3xl">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {failedToLoadPlans ? (
          <Alert variant="destructive" className="mx-auto max-w-3xl">
            <AlertDescription>
              Não foi possível carregar os planos. Recarregue a página e tente
              novamente.
            </AlertDescription>
          </Alert>
        ) : plans.length === 0 ? (
          <Card className="mx-auto max-w-3xl border border-border/60">
            <CardHeader className="text-center">
              <h2 className="text-2xl font-semibold">
                Nenhum plano disponível
              </h2>
              <CardDescription>
                No momento, nenhum plano de assinatura está disponível. Tente
                novamente mais tarde.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <>
            <section
              className="grid gap-5 md:grid-cols-2"
              aria-label="Planos de assinatura"
            >
              {plans.map((plan: SubscriptionPlan) => {
                const isRecommended = plan.interval === "yearly";
                const isCheckingOut = isLoading && selectedPlan === plan.id;
                const monthlyEquivalentLabel =
                  yearlyValue && isRecommended
                    ? `Equivale a ${formatPrice(
                        yearlyValue.monthlyEquivalent,
                        plan.currency
                      )}/mês`
                    : null;
                const annualSavingsLabel =
                  yearlyValue && isRecommended
                    ? `Economize ${formatPrice(
                        yearlyValue.annualSavings,
                        plan.currency
                      )} por ano`
                    : null;
                const savingsPercentageLabel =
                  yearlyValue && isRecommended
                    ? `${yearlyValue.savingsPercentage}% de economia`
                    : null;
                const showYearlyValue =
                  monthlyEquivalentLabel &&
                  annualSavingsLabel &&
                  savingsPercentageLabel;

                return (
                  <Card
                    key={plan.id}
                    className={cn(
                      "relative overflow-hidden border border-border/70 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg",
                      isRecommended &&
                        "border-primary/50 shadow-[0_18px_60px_rgba(185,0,39,0.12)]"
                    )}
                  >
                    {isRecommended && (
                      <>
                        <div
                          className="absolute inset-x-0 top-0 h-1 bg-primary"
                          aria-hidden="true"
                        />
                        <Badge className="absolute right-6 top-5">
                          Mais escolhido
                        </Badge>
                      </>
                    )}

                    <CardHeader className="gap-2 pr-32">
                      <h2 className="text-2xl font-semibold">
                        {planLabels[plan.interval]}
                      </h2>
                      <CardDescription>
                        {plan.trialPeriodDays > 0
                          ? `${plan.trialPeriodDays} dias grátis`
                          : "Assinatura Swift Menu"}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="flex flex-1 flex-col gap-5">
                      <div>
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                          <span className="font-headline text-4xl font-bold tabular-nums text-foreground sm:text-5xl">
                            {formatPrice(plan.price, plan.currency)}
                          </span>
                          <span className="text-muted-foreground">
                            {planIntervalLabels[plan.interval]}
                          </span>
                        </div>
                        {showYearlyValue && (
                          <div className="mt-3 space-y-1">
                            <p className="text-sm font-medium text-foreground">
                              {monthlyEquivalentLabel}
                            </p>
                            <p className="text-sm text-primary">
                              {annualSavingsLabel}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {savingsPercentageLabel}
                            </p>
                          </div>
                        )}
                      </div>

                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-center gap-2">
                          <Check
                            className="h-4 w-4 text-primary"
                            aria-hidden="true"
                          />
                          Sem fidelidade durante o teste
                        </li>
                        <li className="flex items-center gap-2">
                          <Check
                            className="h-4 w-4 text-primary"
                            aria-hidden="true"
                          />
                          Pagamento seguro via Stripe
                        </li>
                      </ul>
                    </CardContent>

                    <CardFooter>
                      <Button
                        className="w-full"
                        disabled={isLoading}
                        onClick={() => handleSelectPlan(plan.id)}
                        aria-describedby={`${plan.id}-summary`}
                      >
                        {isCheckingOut ? (
                          <Loader2
                            className="h-4 w-4 animate-spin"
                            aria-hidden="true"
                          />
                        ) : (
                          <CreditCard className="h-4 w-4" aria-hidden="true" />
                        )}
                        {planCtaLabels[plan.interval]}
                      </Button>
                      <span id={`${plan.id}-summary`} className="sr-only">
                        {`Inicia o checkout do plano ${planLabels[plan.interval]}.`}
                      </span>
                    </CardFooter>
                  </Card>
                );
              })}
            </section>

            <section className="mt-6 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]">
              <div className="rounded-xl border border-border/60 bg-card p-6 shadow-ambient">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <BadgeCheck className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">
                      Incluído em todos os planos
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Tudo que o restaurante precisa para publicar e manter o
                      cardápio digital.
                    </p>
                  </div>
                </div>
                <ul className="grid gap-4 sm:grid-cols-2">
                  {includedFeatures.map((feature) => {
                    const Icon = feature.icon;

                    return (
                      <li key={feature.title} className="flex gap-3">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-primary">
                          <Icon className="h-4 w-4" aria-hidden="true" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {feature.title}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {feature.description}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <aside className="rounded-xl border border-border/60 bg-card p-6 shadow-ambient">
                <h2 className="text-xl font-semibold">O que acontece agora</h2>
                <ol className="mt-5 space-y-4">
                  {checkoutSteps.map((step, index) => (
                    <li key={step} className="flex gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                        {index + 1}
                      </span>
                      <span className="text-sm text-muted-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
              </aside>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
